<template>
    <div class="w-full mb-20 items-center flex flex-col gap-4 bg-gray-50">
        <div class="flex items-end justify-center w-full text-xs sticky shadow top-toolbar bg-white p-4">
            <div class="flex w-full items-end justify-start max-w-5xl gap-4">
                <app-select
                    :label="$t('dashboard.selectedBundles')"
                    @input="onSelectedBundlesOptionChange($event)"
                    v-model="selectedBundlesOption"
                >
                    <option value="selectAll" v-if="project_dashboard">{{ $t('dashboard.selectAll') }}</option>
                    <option value="selectAllMOA" v-if="project_dashboard">{{ $t('dashboard.selectAllMOA') }}</option>
                    <option value="selectAllMOE" v-if="project_dashboard">{{ $t('dashboard.selectAllMOE') }}</option>
                    <option value="selectAllCompany" v-if="project_dashboard">
                        {{ $t('dashboard.selectAllCompany') }}
                    </option>
                    <option value="selectMyBundles">
                        {{ $t('dashboard.selectMyBundles') }}
                    </option>
                    <option v-for="bundle in allowedBundles" :value="bundle.id">
                        {{ (bundle.reference ? '#' + bundle.reference + ' ' : '') + bundle.name }}
                    </option>
                </app-select>
                <hsc-menu-style-white>
                    <hsc-menu-button-menu>
                        <app-button icon="icon-filter" size="mini" :label="$t('commons.phasePlural')">
                            <icon-chevron-down width="16px" heigth="16px" />
                        </app-button>
                        <template slot="contextmenu">
                            <hsc-menu-item
                                :label="$t('dashboard.allPhases')"
                                @click="allPhases"
                                :disabled="
                                    EXEChecked &&
                                    OPRChecked &&
                                    ReceiptChecked &&
                                    OPLChecked &&
                                    DeliveryChecked &&
                                    APAChecked
                                "
                            />
                            <hsc-menu-item
                                v-model="CONChecked"
                                :label="$t('commons.phases.CON')"
                                :disabled="!isCONAllowed"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="EXEChecked"
                                :label="$t('commons.phases.EXE')"
                                :disabled="!isEXEAllowed"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="OPRChecked"
                                :label="$t('commons.phases.OPR')"
                                :disabled="!isOPRAllowed"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="ReceiptChecked"
                                :label="$t('commons.phases.Receipt')"
                                :disabled="!isOPRAllowed"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="OPLChecked"
                                :label="$t('commons.phases.OPL')"
                                :disabled="!isOPLAllowed"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="DeliveryChecked"
                                :label="$t('commons.phases.Delivery')"
                                :disabled="!isOPLAllowed"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="APAChecked"
                                :label="$t('commons.phases.APA')"
                                :disabled="!isOPLAllowed"
                                @input="saveFilters"
                            />
                        </template>
                    </hsc-menu-button-menu>
                </hsc-menu-style-white>
                <hsc-menu-style-white>
                    <hsc-menu-button-menu>
                        <app-button icon="icon-filter" size="mini" :label="$t('dashboard.observationTypes')">
                            <icon-chevron-down width="16px" heigth="16px" />
                        </app-button>
                        <template slot="contextmenu">
                            <hsc-menu-item
                                :label="$t('dashboard.allObservationTypes')"
                                @click="allObservationTypes"
                                :disabled="
                                    onDoneWorkChecked && onTodoWorkChecked && onAdministrativeChecked && otherChecked
                                "
                            />
                            <hsc-menu-item
                                v-model="onAdministrativeChecked"
                                :label="$t('observations.types.administrative')"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="onTodoWorkChecked"
                                :label="$t('observations.types.onTodoWork')"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="onDoneWorkChecked"
                                :label="$t('observations.types.onDoneWork')"
                                @input="saveFilters"
                            />
                            <hsc-menu-item
                                v-model="otherChecked"
                                :label="$t('observations.types.other')"
                                @input="saveFilters"
                            />
                        </template>
                    </hsc-menu-button-menu>
                </hsc-menu-style-white>
            </div>
        </div>
        <div class="flex flex-col gap-5 items-center w-full max-w-5xl">
            <app-dashboard-pie
                :title="$t('dashboard.preparationStatusTitle')"
                :series="preparationsData.series"
                :legend-title="$t('dashboard.detailedSpreading')"
                :data="preparationsData.data"
                v-if="EXEChecked || CONChecked"
            >
                <template v-slot:icon>
                    <div class="p-3 rounded-full border-2 text-gray-800">
                        <icon-file-outline width="24px" height="24px" />
                    </div>
                </template>
                <template v-slot:legendFooter>
                    <p>
                        {{ $t('dashboard.averageDelay') }} :
                        <span class="font-semibold">
                            {{ (preparationsData.averageDelay > 0 ? '+' : '') + preparationsData.averageDelay }}
                            {{ $t('commons.dayUnit') }}
                        </span>
                    </p>
                </template>
            </app-dashboard-pie>
            <app-dashboard-pie
                :title="$t('dashboard.preparationVisaStatusTitle')"
                :series="preparationVisas.series"
                :legend-title="$t('dashboard.detailedSpreading')"
                :data="preparationVisas.data"
                v-if="EXEChecked || CONChecked"
            >
                <template v-slot:icon>
                    <div class="p-3 rounded-full border-2 text-gray-800">
                        <icon-eye width="24px" height="24px" />
                    </div>
                </template>
                <template v-slot:legendFooter>
                    <p>
                        {{ $t('dashboard.averageDelay') }} :
                        <span class="font-semibold">
                            {{ (preparationVisas.averageDelay > 0 ? '+' : '') + preparationVisas.averageDelay }}
                            {{ $t('commons.dayUnit') }}
                        </span>
                    </p>
                </template>
            </app-dashboard-pie>
            <app-dashboard-pie
                :title="$t('dashboard.preparationVisaConclusionTitle')"
                :series="preparationConclusionVisas.series"
                :legend-title="$t('dashboard.detailedSpreading')"
                :data="preparationConclusionVisas.data"
                v-if="EXEChecked || CONChecked"
            >
                <template v-slot:icon>
                    <div class="p-3 rounded-full border-2 text-gray-800">
                        <icon-check width="24px" height="24px" />
                    </div>
                </template>
            </app-dashboard-pie>
            <app-dashboard-pie
                :title="$t('dashboard.preparationObservationStatusTitle')"
                :series="observationsStatus.series"
                :legend-title="$t('dashboard.detailedSpreading')"
                :data="observationsStatus.data"
            >
                <template v-slot:icon>
                    <div class="p-3 rounded-full border-2 text-gray-800">
                        <icon-alert-outline width="24px" height="24px" />
                    </div>
                </template>
                <template v-slot:legendFooter>
                    <p>
                        {{ $t('dashboard.averageDelay') }} :
                        <span class="font-semibold">
                            {{ (observationsStatus.averageDelay > 0 ? '+' : '') + observationsStatus.averageDelay }}
                            {{ $t('commons.dayUnit') }}
                        </span>
                    </p>
                </template>
            </app-dashboard-pie>
            <app-dashboard-line-chart
                :data="observationsLine"
                :title="$t('dashboard.preparationObservationLineTitle')"
                :options="observationsLineOptions"
            ></app-dashboard-line-chart>
            <app-dashboard-bar
                :data="visasLine"
                :title="$t('dashboard.visaLineTitle')"
                :options="visasLineOptions"
            ></app-dashboard-bar>
            <div class="p-5 bg-white rounded-lg shadow w-full">
                <div class="flex items-center mb-4 text-gray-800 flex gap-2">
                    <div class="p-3 rounded-full border-2">
                        <icon-gauge width="24px" height="24px" />
                    </div>
                    <h2 class="text-lg font-semibold text-gray-800">{{ $t('dashboard.bundlesPerformance') }}</h2>
                </div>
                <div class="overflow-x-auto">
                    <table class="min-w-full">
                        <thead>
                            <tr class="bg-gray-50">
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <button
                                        v-if="sortBy === 'name'"
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortAsc = !sortAsc"
                                    >
                                        {{ $t('bundles.bundle') }}
                                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                        <icon-menu-down v-else width="16" height="16" />
                                    </button>
                                    <button
                                        v-else
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortBy = 'name'"
                                    >
                                        {{ $t('bundles.bundle') }}
                                    </button>
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    style="min-width: 20em"
                                    :title="$t('dashboard.scoreTips')"
                                >
                                    <button
                                        v-if="sortBy === 'score'"
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortAsc = !sortAsc"
                                    >
                                        {{ $t('dashboard.score') }}
                                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                        <icon-menu-down v-else width="16" height="16" />
                                    </button>
                                    <button
                                        v-else
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortBy = 'score'"
                                    >
                                        {{ $t('dashboard.score') }}
                                    </button>
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    style="width: 4em"
                                >
                                    <button
                                        v-if="sortBy === 'taskMeanDelay'"
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortAsc = !sortAsc"
                                    >
                                        {{ $t('dashboard.taskMeanDelay') }}
                                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                        <icon-menu-down v-else width="16" height="16" />
                                    </button>
                                    <button
                                        v-else
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortBy = 'taskMeanDelay'"
                                    >
                                        {{ $t('dashboard.taskMeanDelay') }}
                                    </button>
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    style="width: 4em"
                                >
                                    <button
                                        v-if="sortBy === 'attendance'"
                                        class="hover:underline flex justify-center w-full"
                                        :title="$t('dashboard.attendanceTips')"
                                        @click="sortAsc = !sortAsc"
                                    >
                                        {{ $t('dashboard.attendance') }}
                                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                        <icon-menu-down v-else width="16" height="16" />
                                    </button>
                                    <button
                                        v-else
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortBy = 'attendance'"
                                        :title="$t('dashboard.attendanceTips')"
                                    >
                                        {{ $t('dashboard.attendance') }}
                                    </button>
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    style="width: 4em"
                                >
                                    <button
                                        v-if="sortBy === 'observations'"
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortAsc = !sortAsc"
                                        :title="$t('dashboard.observationsTips')"
                                    >
                                        {{ $t('dashboard.observations') }}
                                        <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                        <icon-menu-down v-else width="16" height="16" />
                                    </button>
                                    <button
                                        v-else
                                        class="hover:underline flex justify-center w-full"
                                        @click="sortBy = 'observations'"
                                        :title="$t('dashboard.observationsTips')"
                                    >
                                        {{ $t('dashboard.observations') }}
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr
                                v-for="(bundlePerformance, index) in performances"
                                :key="index"
                                class="hover:bg-gray-50"
                            >
                                <td class="px-6 py-4 text-sm font-medium text-gray-800">
                                    {{ bundlePerformance.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    <div class="flex items-center">
                                        <div class="w-full bg-gray-200 rounded-full h-2 mr-2">
                                            <div
                                                v-if="bundlePerformance.score"
                                                :class="`rounded-full h-2 ${
                                                    bundlePerformance.score >= 70
                                                        ? 'bg-green-600'
                                                        : bundlePerformance.score >= 60
                                                        ? 'bg-yellow-500'
                                                        : 'bg-red-500'
                                                }`"
                                                :style="{ width: `${bundlePerformance.score}%` }"
                                            ></div>
                                        </div>
                                        <span v-if="bundlePerformance.score">{{ bundlePerformance.score }}%</span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 flex justify-end">
                                    <span
                                        v-if="
                                            bundlePerformance.taskMeanDelay > 0 && bundlePerformance.taskMeanDelay < 100
                                        "
                                    >
                                        +
                                    </span>
                                    <span v-if="bundlePerformance.taskMeanDelay >= 100">&gt;</span>
                                    {{ bundlePerformance.taskMeanDelay }}
                                    <span v-if="bundlePerformance.taskMeanDelay !== ''">%</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {{ bundlePerformance.attendance }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {{ bundlePerformance.observations }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import AppBarChart from '@/components/app-barChart/AppBarChart';
import AppBundle from '@/components/app-bundle/appBundle';
import AppSeparator from '@/components/appSeparator/AppSeparator';
import IconCheck from '@/icons/IconCheck';
import { sortBy, uniqBy } from '@/services/sanitize.service';
import { getPlannedTasks } from '@/features/tasks/plannedTasks.service';
import { getAllPreparationsWithVisa } from '@/features/preparations/preparations.service';
import AppTips from '@/components/app-tips/AppTips';
import DashboardEmissionBars from '@/features/dashboards/DashboardEmissionBars';
import DashboardDelaysComparisonBars from './DashboardDelaysComparisonBars';
import AppSelect from '@/components/appSelect/AppSelect';
import FollowObservationStats from '@/features/follow/FollowObservationStats';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { getProject } from '@/features/projects/projects.service';
import { getBundles } from '@/features/bundles/bundles.service';
import { getObservations } from '@/features/observations/observation.service';
import { getLocationsTree } from '@/features/locations/locations.service';
import { getAgenda } from '@/features/planning/agenda/agenda.service';
import { getSupports } from '@/features/supports/supports.service';
import { getCertificates } from '@/features/certificates/certificates.service';
import { getAutoControls } from '@/features/autoControls/autoControls.service';
import { getReceptions } from '@/features/receptions/receptions.service';
import AppDashboardTiles from '@/components/app-dashboard-tiles/AppDashboardTiles.vue';
import AppDashboardPie from '@/components/app-dashboard-pie/AppDashboardPie.vue';
import AppDropDownButton from '@/components/appDropDownButton/AppDropDownButton.vue';
import IconChevronDown from '@/icons/IconChevronDown.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import IconFileOutline from '@/icons/IconFileOutline.vue';
import IconFileCheckOutline from '@/icons/IconFileCheckOutline.vue';
import { getMeetings } from '@/features/meetings/meetings.service';
import HeaderCell from '@/components/gantt/HeaderCell.vue';
import AppDashboardHorizontalBar from '@/components/app-dashboard-horizontal-bar/AppDashboardHorizontalBar.vue';
import AppDashboardLineChart from '@/components/app-dashboard-line-chart/AppDashboardLineChart.vue';

import { format, previousMonday } from 'date-fns';
import startOfDay from 'date-fns/startOfDay';
import AppDashboardBar from '@/components/app-dashboard-bar/AppDashboardBar.vue';

export default {
    components: {
        AppDashboardBar,
        AppDashboardLineChart,
        AppDashboardHorizontalBar,
        HeaderCell,
        IconFileCheckOutline,
        IconFileOutline,
        AppButton,
        IconChevronDown,
        AppDropDownButton,
        AppDashboardPie,
        AppDashboardTiles,
        FollowObservationStats,
        AppSelect,
        DashboardEmissionBars,
        AppTips,
        IconCheck,
        AppSeparator,
        AppBundle,
        AppBarChart,
        DashboardDelaysComparisonBars,
    },
    async created() {
        this.subscriptions = [
            combineLatest([
                getPlannedTasks(this.$route.params.projectId, new BehaviorSubject(new Date())),
                getProject(this.$route.params.projectId),
                getObservations(this.$route.params.projectId),
                getLocationsTree(this.$route.params.projectId),
                getAgenda(this.$route.params.projectId),
                getSupports(this.$route.params.projectId),
                getAllPreparationsWithVisa(this.$route.params.projectId),
                getCertificates(this.$route.params.projectId),
                getReceptions(this.$route.params.projectId),
                getAutoControls(this.$route.params.projectId),
                getBundles(this.$route.params.projectId),
                getMeetings(this.$route.params.projectId),
            ]).subscribe(
                async ([
                    tasks,
                    project,
                    observations,
                    folders,
                    agenda,
                    supports,
                    preparations,
                    certificates,
                    receptions,
                    autoControls,
                    bundles,
                    meetings,
                ]) => {
                    this.meetings = meetings;
                    this.tasks = tasks;

                    this.project = project;

                    this.isCONAllowed = project.projectFeatures.includes('project_CON');
                    this.isEXEAllowed = project.projectFeatures.includes('project_EXE');
                    this.isOPRAllowed = project.projectFeatures.includes('project_OPR');
                    this.isOPLAllowed = project.projectFeatures.includes('project_OPL');

                    this.observations = observations;
                    this.folders = folders;
                    this.agenda = agenda;
                    this.supports = supports;
                    this.preparationsGroups = uniqBy(preparations, 'groupId');
                    this.preparations = preparations.map((preparation) => ({
                        ...preparation,
                        isLastVersion: !!this.preparationsGroups.find(({ id }) => id === preparation.id),
                    }));
                    this.certificates = certificates;
                    this.autoControls = autoControls;
                    this.receptions = receptions;
                    this.bundles = bundles;
                    this.me = {
                        ...project.me,
                        bundles: project.me.bundleIds.map((bundleId) =>
                            bundles.find((bundle) => bundle.id === bundleId),
                        ),
                    };
                    this.selectedBundles = this.bundles.filter((bundle) => this.me.bundleIds.includes(bundle.id));
                    this.restoreFilters();
                },
            ),
        ];
    },
    methods: {
        meanByWeeks(data, aggregate) {
            const dates = data.reduce((acc, event) => {
                const key = startOfDay(previousMonday(event.date)).getTime();
                if (acc[key]) {
                    acc[key].push(event.value);
                } else {
                    acc[key] = [event.value];
                }
                return acc;
            }, {});
            return Object.keys(dates)
                .sort()
                .reduce((acc, key) => {
                    acc.push({
                        y: aggregate(dates[key]),
                        x: parseInt(key),
                        label: format(parseInt(key), "yyyy-MM-dd' S'ww"),
                    });
                    return acc;
                }, []);
        },
        saveFilters() {
            localStorage.setItem(
                'groupDashoard.filter.' + this.$route.params.projectId,
                JSON.stringify({
                    CONChecked: this.CONChecked,
                    EXEChecked: this.EXEChecked,
                    OPRChecked: this.OPRChecked,
                    ReceiptChecked: this.ReceiptChecked,
                    OPLChecked: this.OPLChecked,
                    DeliveryChecked: this.DeliveryChecked,
                    APAChecked: this.APAChecked,
                    onDoneWorkChecked: this.onDoneWorkChecked,
                    onTodoWorkChecked: this.onTodoWorkChecked,
                    onAdministrativeChecked: this.onAdministrativeChecked,
                    otherChecked: this.otherChecked,
                    selectedBundles: this.selectedBundles.map((bundle) => bundle.id),
                }),
            );
        },
        restoreFilters() {
            const item = localStorage.getItem('groupDashoard.filter.' + this.$route.params.projectId);
            if (item) {
                const state = JSON.parse(item);
                this.CONChecked = state.CONChecked;
                this.EXEChecked = state.EXEChecked;
                this.OPRChecked = state.OPRChecked;
                this.ReceiptChecked = state.ReceiptChecked;
                this.OPLChecked = state.OPLChecked;
                this.DeliveryChecked = state.DeliveryChecked;
                this.APAChecked = state.APAChecked;
                this.onDoneWorkChecked = state.onDoneWorkChecked;
                this.onTodoWorkChecked = state.onTodoWorkChecked;
                this.onAdministrativeChecked = state.onAdministrativeChecked;
                this.otherChecked = state.otherChecked;
                this.selectedBundles = this.bundles.filter((bundle) => state.selectedBundles.includes(bundle.id));
                this.selectedBundlesOption = state.selectedBundles[0];
            }
        },
        allPhases() {
            this.CONChecked = true;
            this.EXEChecked = true;
            this.OPRChecked = true;
            this.ReceiptChecked = true;
            this.OPLChecked = true;
            this.DeliveryChecked = true;
            this.APAChecked = true;

            this.saveFilters();
        },
        allObservationTypes() {
            this.onDoneWorkChecked = true;
            this.onTodoWorkChecked = true;
            this.onAdministrativeChecked = true;
            this.otherChecked = true;
            this.saveFilters();
        },
        async onSelectedBundlesOptionChange(value) {
            if (value === 'selectAll') {
                this.selectedBundles = this.bundles;
            } else if (value === 'selectAllMOA') {
                this.selectedBundles = this.bundles.filter((bundle) => bundle.category === 'MOA');
            } else if (value === 'selectAllMOE') {
                this.selectedBundles = this.bundles.filter((bundle) => bundle.category === 'MOE');
            } else if (value === 'selectAllCompany') {
                this.selectedBundles = this.bundles.filter((bundle) => bundle.category === 'company');
            } else if (value === 'selectMyBundles') {
                this.selectedBundles = this.me.bundles;
            } else {
                this.selectedBundles = this.bundles.filter((bundle) => bundle.id === value);
            }
            this.saveFilters();
        },
        isBundle(recipientId) {
            return (
                recipientId &&
                this.selectedBundles.some(({ id }) => {
                    return recipientId === id;
                })
            );
        },
        getSerie(name, array, filter, color) {
            const filteredItem = array.filter(filter);
            return {
                name: this.$t(name),
                value: filteredItem.length ? Math.round((filteredItem.length / array.length) * 100) : 0,
                count: filteredItem.length,
                color,
            };
        },
        seriesToData(series) {
            return {
                labels: series.map((item) => item.name),
                datasets: [
                    {
                        data: series.map((item) => item.value),
                        backgroundColor: series.map((item) => item.color),
                        hoverBackgroundColor: series.map((item) => item.color),
                    },
                ],
            };
        },
        computeAttendanceScore(meetings, bundleId) {
            let attendanceSum = 0;
            let attendanceCount = 0;
            for (const meeting of meetings.filter((meeting) => meeting.date < new Date())) {
                const convocation = meeting.convocations.find((convocation) => convocation.bundleId === bundleId);
                if (convocation) {
                    attendanceCount++;
                    if (convocation.attendance === 'present') {
                        attendanceSum++;
                    } else if (convocation.attendance === 'excusedAbsent') {
                        attendanceSum += 0.5;
                    }
                }
            }
            if (!attendanceCount) return null;
            return attendanceSum / attendanceCount;
        },
        computeTaskMeanDelay(tasks, bundleId) {
            const relevantTasks = tasks.filter((task) => task.service.bundle.id === bundleId);
            if (!relevantTasks.length) return null;
            return (
                1 -
                Math.min(
                    1,
                    Math.max(
                        0,
                        relevantTasks.reduce((acc, task) => acc + task.late / task.duration, 0) / relevantTasks.length,
                    ),
                )
            );
        },
        computeObservationMeanResolutionDelay(observations, bundleId) {
            const relevantObservations = observations.filter(
                (observation) =>
                    observation.recipientIds.includes(bundleId) &&
                    observation.dueDate &&
                    observation.resolvedAt &&
                    observation.validatedAt,
            );
            if (!relevantObservations.length) return 0;
            return (
                relevantObservations.reduce(
                    (acc, observation) => acc - observation.dueDate.getTime() + observation.resolvedAt.getTime(),
                    0,
                ) /
                relevantObservations.length /
                86400000
            );
        },
        computeTaskScore(tasks, bundleId) {
            const relevantTasks = tasks.filter((task) => task.service.bundle.id === bundleId);
            if (!relevantTasks.length) return null;
            return Math.max(
                relevantTasks.reduce((acc, task) => {
                    const inTimeBonus = task.late < 3 ? 1 : -1;
                    const durationBonus = task.duration >= task.realDuration ? 1 : 0;
                    const startingBonus = task.realStartDate?.getTime() <= task.startDate.getTime() ? 1 : 0;
                    return acc + inTimeBonus + durationBonus + startingBonus;
                }, 0) /
                    (relevantTasks.length * 3),
                0,
            );
        },
        computeDocScore(preparations, bundleId) {
            const emittedPreparations = preparations.filter(
                (preparation) =>
                    bundleId === preparation.bundleId &&
                    ((preparation.phase === 'con' && this.CONChecked) ||
                        (preparation.phase === 'exe' && this.EXEChecked)) &&
                    preparation.emissionDueDate &&
                    ((preparation.emissionDate && preparation.emissionDate > preparation.emissionDueDate) ||
                        (preparation.isLastVersion && preparation.emissionDueDate.getTime() < Date.now())),
            );
            if (!emittedPreparations.length) return null;
            return (
                emittedPreparations.reduce((acc, preparation) => {
                    const late =
                        (new Date(preparation.emissionDueDate).getTime() -
                            new Date(preparation.emissionDate || new Date()).getTime()) /
                        86400000;
                    return acc + (late <= 1 ? 1 : late <= 3 ? 0.5 : 0);
                }, 0) / emittedPreparations.length
            );
        },
        computeObservationsScore(observations, bundleId) {
            const resolvedObservations = observations.filter(
                (observation) =>
                    bundleId === observation.recipientIds.includes(bundleId) &&
                    observation.dueDate &&
                    !!observation.validatedAt &&
                    ((!!observation.resolvedAt && observation.resolvedAt > observation.dueDate) ||
                        observation.dueDate.getTime() < Date.now()),
            );
            if (!resolvedObservations.length) return null;
            return (
                resolvedObservations.reduce((acc, observation) => {
                    const late =
                        (new Date(observation.dueDate).getTime() -
                            new Date(observation.resolvedAt || new Date()).getTime()) /
                        86400000;
                    return acc + late <= 1 ? 1 : 0;
                }, 0) / resolvedObservations.length
            );
        },
    },
    computed: {
        filteredObservations() {
            return this.observations.filter(
                (observation) =>
                    ((observation.type === 'administrative' && this.onAdministrativeChecked) ||
                        (observation.type === 'other' && this.otherChecked) ||
                        (observation.type === 'onDoneWork' && this.onDoneWorkChecked) ||
                        (observation.type === 'onTodoWork' && this.onTodoWorkChecked)) &&
                    (observation.recipientIds
                        ? observation.recipientIds.some((recipient) => this.isBundle(recipient))
                        : false) &&
                    this.filteredPhases.includes(observation.phase),
            );
        },
        performances() {
            const bundles = this.selectedBundles.map((bundle) => {
                const docScore = this.computeDocScore(this.preparations, bundle.id);
                const taskDelay = this.computeTaskMeanDelay(this.tasks, bundle.id);
                const taskScore = this.computeTaskScore(this.tasks, bundle.id);
                const attendance = this.computeAttendanceScore(this.meetings, bundle.id);
                const observations = this.computeObservationMeanResolutionDelay(this.filteredObservations, bundle.id);
                const observationsScore = this.computeObservationsScore(this.filteredObservations, bundle.id);
                const totals = [docScore, taskScore, taskDelay, attendance, observationsScore].filter(
                    (a) => a !== null,
                );
                return {
                    name: `${bundle.reference} - ${bundle.name}`,
                    score: totals.length
                        ? Math.round((totals.reduce((acc, num) => acc + num, 0) / totals.length) * 100)
                        : '',
                    taskMeanDelay: taskDelay !== null ? Math.round(100 - taskDelay * 100) : '',
                    attendance: attendance !== null ? Math.round(attendance * 100) + '%' : '',
                    observations: observations !== null ? Math.round(observations) + 'j' : '',
                };
            });
            if (this.sortAsc) {
                return sortBy(bundles, this.sortBy);
            } else {
                return sortBy(bundles, this.sortBy).reverse();
            }
        },
        filteredPhases() {
            return [
                ...(this.CONChecked ? ['CON'] : []),
                ...(this.EXEChecked ? ['EXE'] : []),
                ...(this.APAChecked ? ['APA'] : []),
                ...(this.OPRChecked ? ['OPR'] : []),
                ...(this.OPLChecked ? ['OPL'] : []),
                ...(this.DeliveryChecked ? ['Delivery'] : []),
                ...(this.ReceiptChecked ? ['Receipt'] : []),
            ];
        },
        preparationVisas() {
            let visas = this.preparationsGroups
                .filter((p) => (p.phase === 'con' && this.CONChecked) || (p.phase === 'exe' && this.EXEChecked))
                .reduce(
                    (acc, preparation) => [...acc, ...preparation.visas.map((visa) => ({ ...visa, preparation }))],
                    [],
                )
                .filter((visa) => this.isBundle(visa.emitterId));
            let singleVisas = this.preparations
                .filter((p) => (p.phase === 'con' && this.CONChecked) || (p.phase === 'exe' && this.EXEChecked))
                .reduce(
                    (acc, preparation) => [...acc, ...preparation.visas.map((visa) => ({ ...visa, preparation }))],
                    [],
                )
                .filter(
                    (visa) =>
                        this.isBundle(visa.emitterId) &&
                        visa.emissionDate &&
                        visa.emissionDueDate &&
                        visa.preparation.emissionDate,
                );

            const averageDelay = singleVisas.length
                ? Math.round(
                      singleVisas.reduce(
                          (acc, visa) =>
                              acc + new Date(visa.emissionDate).getTime() - new Date(visa.emissionDueDate).getTime(),
                          0,
                      ) /
                          singleVisas.length /
                          86400000,
                  )
                : 0;
            const series = [
                this.getSerie('dashboard.visaPaused', visas, (visa) => !visa.preparation.emissionDate, '#6B7280'),
                this.getSerie(
                    'dashboard.visaExpected',
                    visas,
                    (visa) => !!visa.preparation.emissionDate && !visa.conclusion,
                    '#FBBF24',
                ),

                this.getSerie(
                    'dashboard.visaCompleted',
                    visas,
                    (visa) => !!visa.preparation.emissionDate && visa.conclusion,
                    '#10B981',
                ),
            ];
            return {
                series,
                averageDelay,
                total: visas.length,
                data: this.seriesToData(series),
            };
        },
        preparationConclusionVisas() {
            let visas = this.preparationsGroups
                .filter((p) => (p.phase === 'con' && this.CONChecked) || (p.phase === 'exe' && this.EXEChecked))
                .reduce((acc, preparation) => [...acc, ...preparation.visas], [])
                .filter((visa) => this.isBundle(visa.emitterId) && !!visa.conclusion);
            const series = [
                this.getSerie('dashboard.visaApproved', visas, (visa) => visa.conclusion === 'approved', '#10B981'),
                this.getSerie(
                    'dashboard.visaApprovedWithComments',
                    visas,
                    (visa) => visa.conclusion === 'approvedWithComments',
                    '#FBBF24',
                ),
                this.getSerie('dashboard.visaRejected', visas, (visa) => visa.conclusion === 'rejected', '#EF4444'),
                this.getSerie(
                    'dashboard.visaNonCompliant',
                    visas,
                    (visa) => visa.conclusion === 'nonCompliant',
                    '#000',
                ),
            ];
            return {
                series,
                total: visas.length,
                data: this.seriesToData(series),
            };
        },
        observationsStatus() {
            let observations = this.filteredObservations;
            const filteredObservations = observations.filter(
                (observation) => !observation.obsoleteAt && !!observation.dueDate && !!observation.resolvedAt,
            );
            const averageDelay = observations.length
                ? Math.round(
                      filteredObservations.reduce(
                          (acc, observation) =>
                              acc +
                              new Date(observation.resolvedAt).getTime() -
                              new Date(observation.dueDate).getTime(),
                          0,
                      ) /
                          filteredObservations.length /
                          86400000,
                  )
                : 0;

            const series = [
                this.getSerie(
                    'observations.toFix',
                    observations,
                    (observation) =>
                        !observation.obsoleteAt &&
                        !observation.validatedAt &&
                        !observation.resolvedAt &&
                        (!observation.dueDate || new Date(observation.dueDate).getTime() > Date.now()),
                    '#ef4444',
                ),
                this.getSerie(
                    'dashboard.observationToFixLate',
                    observations,
                    (observation) =>
                        !observation.obsoleteAt &&
                        !observation.validatedAt &&
                        !observation.resolvedAt &&
                        observation.dueDate &&
                        new Date(observation.dueDate).getTime() < Date.now(),
                    '#b51212',
                ),
                this.getSerie('observations.obsolete', observations, (observation) => !!observation.obsoleteAt, '#000'),
                this.getSerie(
                    'dashboard.observationFixed',
                    observations,
                    (observation) => !observation.obsoleteAt && !observation.validatedAt && observation.resolvedAt,
                    '#86efac',
                ),
                this.getSerie(
                    'observations.validated',
                    observations,
                    (observation) => !observation.obsoleteAt && !!observation.validatedAt,
                    '#10B981',
                ),
            ];
            return {
                series,
                total: observations.length,
                averageDelay,
                data: this.seriesToData(series),
            };
        },
        preparationsData() {
            let preparations = this.preparations
                .filter((p) => (p.phase === 'con' && this.CONChecked) || (p.phase === 'exe' && this.EXEChecked))
                .filter(
                    (preparation) =>
                        this.isBundle(preparation.bundleId) &&
                        !!preparation.emissionDueDate &&
                        (preparation.emissionDate ||
                            (preparation.isLastVersion && new Date(preparation.emissionDueDate) < Date.now())),
                );
            const averageDelay = preparations.length
                ? Math.round(
                      preparations.reduce(
                          (acc, preparation) =>
                              acc +
                              (preparation.emissionDate ? new Date(preparation.emissionDate).getTime() : Date.now()) -
                              new Date(preparation.emissionDueDate).getTime(),
                          0,
                      ) /
                          preparations.length /
                          86400000,
                  )
                : 0;
            const series = [
                this.getSerie(
                    'dashboard.preparationPaused',
                    preparations,
                    (preparation) =>
                        !preparation.emissionDate &&
                        (!preparation.emissionDueDate || new Date(preparation.emissionDueDate) > Date.now()),
                    '#6B7280',
                ),
                this.getSerie(
                    'dashboard.preparationExpected',
                    preparations,
                    (preparation) =>
                        !preparation.emissionDate &&
                        preparation.emissionDueDate &&
                        new Date(preparation.emissionDueDate) < Date.now(),
                    '#FBBF24',
                ),
                this.getSerie(
                    'dashboard.preparationEmitted',
                    preparations,
                    (preparation) => !!preparation.emissionDate,
                    '#10B981',
                ),
            ];
            return {
                series,
                averageDelay,
                total: preparations.length,
                data: this.seriesToData(series),
            };
        },
        project_dashboard() {
            return this.me.allowedFeatures.includes('project_dashboard');
        },
        allowedBundles() {
            if (this.me.allowedFeatures.includes('project_dashboard')) {
                return this.bundles;
            } else {
                return this.me.bundleIds.map((bundleId) => this.bundles.find((aBundle) => aBundle.id === bundleId));
            }
        },
        observationsLine() {
            const openObservationEvents = this.filteredObservations
                .filter((observation) => !!observation.dueDate)
                .map((observation) => ({
                    date: new Date(observation.reportedAt || observation.createdAt),
                    value: 1,
                }));
            const closingObservationEvents = this.filteredObservations
                .filter((observation) => !!observation.dueDate && !!(observation.obsoleteAt || observation.resolvedAt))
                .map((observation) => ({
                    date: new Date(observation.obsoleteAt || observation.resolvedAt),
                    value: -1,
                }));
            const expectedClosingObservationEvents = this.filteredObservations
                .filter((observation) => !!observation.dueDate)
                .map((observation) => ({
                    date: new Date(observation.dueDate || observation.obsoleteAt),
                    value: -1,
                }));

            let previousWeek = 0;
            const data = this.meanByWeeks(
                [...openObservationEvents, ...closingObservationEvents],
                (events) => {
                    previousWeek = previousWeek + events.reduce((acc, val) => acc + val, 0);
                    return previousWeek;
                },
                [],
            );
            previousWeek = 0;
            const expectedData = this.meanByWeeks(
                [...openObservationEvents, ...expectedClosingObservationEvents],
                (events) => {
                    previousWeek = previousWeek + events.reduce((acc, val) => acc + val, 0);
                    return previousWeek;
                },
                [],
            );
            const today = startOfDay(previousMonday(new Date())).getTime();
            data.push({
                y: this.filteredObservations.filter(
                    (observation) => !observation.obsoleteAt && !observation.validatedAt && !observation.resolvedAt,
                ).length,
                x: today,
                label: format(today, "yyyy-MM-dd' S'ww"),
            });
            return {
                datasets: [
                    {
                        label: this.$t('dashboard.observationLineTitle'),
                        data,
                        borderColor: '#3B82F6',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        tension: 0.1,
                    },
                    {
                        label: this.$t('dashboard.observationLineExpectedTitle'),
                        data: expectedData,
                        borderColor: 'rgb(251, 191, 36)',
                        backgroundColor: 'rgb(251, 191, 36)',
                        tension: 0.1,
                    },
                ],
            };
        },
        visasLine() {
            const data = this.meanByWeeks(
                this.preparations
                    .filter((p) => (p.phase === 'con' && this.CONChecked) || (p.phase === 'exe' && this.EXEChecked))
                    .reduce(
                        (acc, preparation) => [
                            ...acc,
                            ...preparation.visas.map((visa) => ({
                                ...visa,
                                preparation,
                            })),
                        ],
                        [],
                    )
                    .filter(
                        (visa) =>
                            this.isBundle(visa.emitterId) &&
                            visa.emissionDate &&
                            visa.emissionDueDate &&
                            visa.preparation.emissionDate,
                    )
                    .map((visa) => {
                        const date = new Date(visa.emissionDate).getTime();
                        const value = date - new Date(visa.emissionDueDate).getTime();
                        return {
                            date,
                            value,
                        };
                    }),
                (events) => Math.round(events.reduce((acc, val) => acc + val, 0) / events.length / 86400000),
            );
            return {
                datasets: [
                    {
                        data,
                        borderWidth: 1,
                        backgroundColor: data.map(({ y }) => (y < 0 ? '#3bf680' : '#f63b82')),
                        borderColor: data.map(({ y }) => (y < 0 ? '#3bf680' : '#f63b82')),
                    },
                ],
            };
        },
        observationsLineOptions() {
            return {
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            minUnit: 'week',
                            unit: 'week',
                            displayFormats: {
                                week: "yyyy' - S'ww",
                            },
                        },
                        min: this.observationsLine.datasets[0].data[0]?.x,
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                    },
                    datalabels: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            title: function (context) {
                                return context[0].raw.label;
                            },
                        },
                    },
                },
            };
        },

        visasLineOptions() {
            return {
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            minUnit: 'week',
                            unit: 'week',
                            displayFormats: {
                                week: "yyyy' - S'ww",
                            },
                        },
                        min: this.visasLine.datasets[0].data[0]?.x,
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                    datalabels: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            title: function (context) {
                                return context[0].raw.label;
                            },
                        },
                    },
                },
            };
        },
    },
    data() {
        return {
            me: { bundleIds: [], bundles: [], allowedFeatures: [] },
            selectedBundlesOption: 'selectMyBundles',
            selectedBundles: [],
            folders: [],
            observations: [],
            tasks: [],
            agenda: [],
            bundles: [],
            autoControls: [],
            certificates: [],
            receptions: [],
            preparations: [],
            preparationsGroups: [],
            project: { projectFeatures: [] },
            isEXEAllowed: false,
            isOPRAllowed: false,
            isCONAllowed: false,
            isOPLAllowed: false,
            CONChecked: true,
            EXEChecked: true,
            OPRChecked: true,
            ReceiptChecked: true,
            OPLChecked: true,
            DeliveryChecked: true,
            APAChecked: true,
            meetings: [],
            sortBy: 'name',
            sortAsc: true,
            onDoneWorkChecked: true,
            onTodoWorkChecked: true,
            onAdministrativeChecked: true,
            otherChecked: true,
        };
    },
};
</script>
